import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { formatDateTimeInTz } from "@/design-system/lib/format";
import { BrokerDealFlowShell } from "../../components/BrokerDealFlowShell";
import { BrokerOnly } from "../../components/BrokerOnly";
import { WaitState } from "../../components/WaitState";
import { dealSubmissionApi } from "../../api/dealSubmission";
import { PortalApiError } from "../../api/client";
import type {
  DealSubmissionAiFeedbackResult,
  DealSubmissionResponseDto,
  DealSubmissionRichDto,
  DealSubmissionSectionDto,
  DealSubmissionSectionStateDto,
  PortalDealSubmissionReturnCommentDto,
} from "../../api/types";
import { DataEntrySection } from "./DataEntrySection";
import { DocumentsSection } from "./DocumentsSection";
import { ReviewAndSubmit } from "./ReviewAndSubmit";
import { ProgressRail } from "./ProgressRail";
import type { EvaluationTracker } from "./evaluationTracker";

// Deal Submission spec §3 / §4 — wizard runner. Loads the rich
// payload (submission + pinned template schema + saved state +
// uploaded docs), renders the section pointed to by the ?section= query
// (default = CurrentSectionOrder), with a left-rail progress indicator.
// A "Review" pseudo-section sits after the last template section.

const REVIEW_KEY = "__review__";

export function DealSubmissionRunner() {
  return (
    <BrokerOnly>
      <RunnerBody />
    </BrokerOnly>
  );
}

function RunnerBody() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<DealSubmissionRichDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const rich = await dealSubmissionApi.get(id);
      setData(rich);
    } catch (err) {
      setError(
        err instanceof PortalApiError ? err.message
          : err instanceof Error ? err.message
          : "Couldn't load this submission.");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const sections = useMemo(
    () => (data ? [...data.sections].sort((a, b) => a.order - b.order) : []),
    [data],
  );
  const activeKey: string = params.get("section") ?? (
    data ? sectionKeyForOrder(sections, data.submission.currentSectionOrder) : ""
  );
  const activeSection = sections.find((s) => sectionKey(s) === activeKey) ?? null;
  const isReview = activeKey === REVIEW_KEY;
  const commentsBySection = useMemo(
    () => (data ? groupReturnCommentsBySection(data) : {}),
    [data],
  );
  const overallReturnComments = useMemo(
    () => (data ? data.returnComments.filter((c) => !commentTargetSectionId(c, data)) : []),
    [data],
  );

  // Switching sections must start the broker at the top of the new form — the
  // page scrolls at the window level (PortalChrome is min-h-screen), so reset
  // it whenever the active section changes rather than preserving scroll.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeKey]);

  // In-flight AI evaluations, keyed by section/document id, tracked HERE (the
  // runner stays mounted across section switches) so a running evaluation is
  // detected when the broker navigates away and back — preventing a duplicate
  // run. The completed result is retained per key so the feedback surfaces
  // immediately on return, independent of the (non-blocking) reload below.
  const [evaluatingKeys, setEvaluatingKeys] = useState<Set<string>>(() => new Set());
  const evaluatingRef = useRef(evaluatingKeys);
  const [evaluationResults, setEvaluationResults] = useState<
    Map<string, DealSubmissionAiFeedbackResult>
  >(() => new Map());
  const runEvaluation = useCallback(
    async (
      key: string,
      fn: () => Promise<DealSubmissionAiFeedbackResult | null>,
    ) => {
      if (evaluatingRef.current.has(key)) return;
      const added = new Set(evaluatingRef.current);
      added.add(key);
      evaluatingRef.current = added;
      setEvaluatingKeys(added);
      let result: DealSubmissionAiFeedbackResult | null = null;
      try {
        result = await fn();
      } finally {
        // Clear the in-flight flag and record the result FIRST so the spinner
        // stops and the feedback shows the moment the run completes. The reload
        // (to refresh persisted-derived state like the freshness gate) is then
        // fire-and-forget — it must never gate the spinner or the result.
        const removed = new Set(evaluatingRef.current);
        removed.delete(key);
        evaluatingRef.current = removed;
        setEvaluatingKeys(removed);
        if (result) {
          setEvaluationResults((prev) => new Map(prev).set(key, result!));
        }
        void load();
      }
    },
    [load],
  );
  const evaluation: EvaluationTracker = useMemo(
    () => ({
      isEvaluating: (key) => evaluatingKeys.has(key),
      result: (key) => evaluationResults.get(key) ?? null,
      run: runEvaluation,
    }),
    [evaluatingKeys, evaluationResults, runEvaluation],
  );

  // A section autosaves to the server but the runner stays mounted across
  // section switches and caches the submission payload. Each section pane is
  // keyed by id, so it REMOUNTS on navigation and re-initialises its form from
  // this cached `sectionStates` — which, without this, still held the answers
  // loaded when the wizard opened. The result: navigating away and back
  // silently reverted the broker's autosaved edits and then overwrote the
  // server with the stale values. Merge the authoritative state the save
  // endpoint returns (it also carries the recomputed evaluation freshness) so a
  // remount re-initialises from the SAVED answers — no full reload needed.
  const applySectionState = useCallback((state: DealSubmissionSectionStateDto) => {
    if (!state?.sectionId) return;
    setData((current) => {
      if (!current) return current;
      const exists = current.sectionStates.some((s) => s.sectionId === state.sectionId);
      return {
        ...current,
        sectionStates: exists
          ? current.sectionStates.map((s) => (s.sectionId === state.sectionId ? state : s))
          : [...current.sectionStates, state],
      };
    });
  }, []);

  function goToKey(key: string) {
    const next = new URLSearchParams(params);
    next.set("section", key);
    setParams(next, { replace: true });
  }

  async function resolveReturnComment(commentId: string) {
    if (!data) return;
    const resolved = await dealSubmissionApi.resolveReturnComment(data.submission.id, commentId);
    setData((current) => current
      ? {
        ...current,
        returnComments: current.returnComments.map((c) => (c.id === commentId ? resolved : c)),
      }
      : current);
  }

  function jumpToFirstAffectedSection() {
    if (!data) return;
    const first = sections.find((s) => (commentsBySection[s.id] ?? []).some((c) => !c.resolved));
    if (first) {
      goToKey(sectionKey(first));
      return;
    }
    const anyTargeted = sections.find((s) => (commentsBySection[s.id] ?? []).length > 0);
    if (anyTargeted) goToKey(sectionKey(anyTargeted));
  }

  function goToNextSection() {
    if (!data) return;
    if (!activeSection) {
      goToKey(REVIEW_KEY);
      return;
    }
    const idx = sections.findIndex((s) => s.id === activeSection.id);
    if (idx === -1 || idx >= sections.length - 1) {
      goToKey(REVIEW_KEY);
    } else {
      goToKey(sectionKey(sections[idx + 1]));
    }
  }

  if (error) {
    return (
      <BrokerDealFlowShell pageTitle="Deal submission">
        <p className="text-small text-status-error" role="alert">{error}</p>
        <Link to="/portal/broker?tab=deals" className="text-small underline mt-space-3 inline-block">
          Back to my deals
        </Link>
      </BrokerDealFlowShell>
    );
  }
  if (!data) {
    return (
      <BrokerDealFlowShell pageTitle="Deal submission">
        <WaitState label="Loading…" message="Pulling up your submission." />
      </BrokerDealFlowShell>
    );
  }

  // Section-scoped reopen: a Draft is fully editable; a returned deal unlocks
  // ONLY the sections the liaison flagged this round (server-authoritative via
  // reopenedSectionIds, so "Mark resolved" never re-locks and the broker can't
  // silently change un-flagged sections); anything else is read-only.
  const isDraft = data.submission.status === "Draft";
  const reopenedSet = new Set(data.reopenedSectionIds ?? []);
  const sectionEditable = (section: DealSubmissionSectionDto) =>
    isDraft || reopenedSet.has(section.id);
  const isReturned = data.submission.status === "ReturnedForChanges";

  return (
    <BrokerDealFlowShell pageTitle="Deal submission">
      {/* RECON→FIX (UAT 2026-05-30) — centered max-width container so the
          form no longer hugs the left with a large dead right gutter. */}
      <div className="mx-auto w-full max-w-5xl flex flex-col gap-space-4 md:flex-row md:gap-space-6">
        <aside className="md:w-64 shrink-0">
          <ProgressRail
            data={data}
            sections={sections}
            activeKey={activeKey}
            onJump={goToKey}
            commentsBySection={commentsBySection}
          />
        </aside>

        <section className="flex-1 min-w-0">
          <header className="flex items-center justify-between gap-space-3">
            <h1 className="text-h2 font-display text-onyx m-0" data-testid="wizard-title">
              {data.submission.title?.trim() || "New deal submission"}
            </h1>
            <StatusChip status={data.submission.status} />
          </header>

          {data.submission.status === "ReturnedForChanges" && data.returnComments.length > 0 && (
            <ReturnCommentsBanner
              comments={data.returnComments}
              overallComments={overallReturnComments}
              onViewComments={jumpToFirstAffectedSection}
              onResolve={resolveReturnComment}
              submissionId={data.submission.id}
              responses={data.responses ?? []}
              onPosted={load}
            />
          )}

          {data.brokerInstructions && (
            <p className="text-small text-gray-mid mt-space-2 whitespace-pre-line">
              {data.brokerInstructions}
            </p>
          )}

          <div className="mt-space-5">
            {isReview ? (
              <ReviewAndSubmit
                data={data}
                sections={sections}
                onEditSection={(s) => goToKey(sectionKey(s))}
                onSubmitted={() => {
                  navigate("/portal/broker?tab=deals", { replace: true });
                }}
              />
            ) : activeSection ? (
              <SectionPane
                key={activeSection.id}
                data={data}
                section={activeSection}
                evaluation={evaluation}
                onSectionStatePersisted={applySectionState}
                editable={sectionEditable(activeSection)}
                readOnlyReason={
                  isReturned && !sectionEditable(activeSection)
                    ? "This section wasn’t flagged for changes, so it’s read-only. Edit only the sections your liaison commented on."
                    : null
                }
                comments={commentsBySection[activeSection.id] ?? []}
                onResolveComment={resolveReturnComment}
                onSaved={async () => {
                  await load();
                }}
                onContinue={async () => {
                  await load();
                  goToNextSection();
                }}
                onExit={() => navigate("/portal/broker?tab=deals", { replace: true })}
              />
            ) : (
              <p className="text-small text-gray-mid">No section selected.</p>
            )}
          </div>
        </section>
      </div>
    </BrokerDealFlowShell>
  );
}

interface SectionPaneProps {
  data: DealSubmissionRichDto;
  section: DealSubmissionSectionDto;
  evaluation: EvaluationTracker;
  onSectionStatePersisted: (state: DealSubmissionSectionStateDto) => void;
  editable: boolean;
  readOnlyReason: string | null;
  onSaved: () => Promise<void>;
  onContinue: () => Promise<void>;
  onExit: () => void;
  comments: PortalDealSubmissionReturnCommentDto[];
  onResolveComment: (commentId: string) => Promise<void>;
}

function SectionPane({
  data,
  section,
  evaluation,
  onSectionStatePersisted,
  editable,
  readOnlyReason,
  onSaved,
  onContinue,
  onExit,
  comments,
  onResolveComment,
}: SectionPaneProps) {
  // RECON→FIX — documents are authored per section; render the uploaders
  // for whichever section owns them (DataEntry or Documents) so nothing is
  // hidden. Both branches derive their document list from the SAME
  // per-section filter the validator uses, so required == visible.
  const sectionDocs = data.documentRequests.filter((r) => r.sectionId === section.id);
  const readOnlyNotice = readOnlyReason ? (
    <p
      className="rounded-md border border-border-light bg-white-warm p-space-3 text-caption text-gray-mid"
      data-testid="section-readonly-notice"
    >
      {readOnlyReason}
    </p>
  ) : null;
  const commentPanel = (comments.length > 0 || readOnlyNotice) ? (
    <>
      {comments.length > 0 ? (
        <ReturnCommentsPanel
          comments={comments}
          onResolve={onResolveComment}
          submissionId={data.submission.id}
          responses={data.responses ?? []}
          onPosted={onSaved}
        />
      ) : null}
      {readOnlyNotice}
    </>
  ) : null;
  if (section.kind === "DataEntry") {
    return (
      <DataEntrySection
        submissionId={data.submission.id}
        section={section}
        sectionState={data.sectionStates.find((s) => s.sectionId === section.id) ?? null}
        editable={editable}
        evaluation={evaluation}
        onSectionStatePersisted={onSectionStatePersisted}
        documentRequests={sectionDocs}
        documents={data.documents}
        onDocumentsChanged={onSaved}
        commentPanel={commentPanel}
        onContinue={onContinue}
        onExit={onExit}
      />
    );
  }
  return (
    <DocumentsSection
      submissionId={data.submission.id}
      section={section}
      sectionState={data.sectionStates.find((s) => s.sectionId === section.id) ?? null}
      documentRequests={sectionDocs}
      documents={data.documents}
      editable={editable}
      evaluation={evaluation}
      commentPanel={commentPanel}
      onChanged={onSaved}
      onContinue={onContinue}
      onExit={onExit}
    />
  );
}


function ReturnCommentsBanner({
  comments,
  overallComments,
  onViewComments,
  onResolve,
  submissionId,
  responses,
  onPosted,
}: {
  comments: PortalDealSubmissionReturnCommentDto[];
  overallComments: PortalDealSubmissionReturnCommentDto[];
  onViewComments: () => void;
  onResolve: (commentId: string) => Promise<void>;
  submissionId: string;
  responses: DealSubmissionResponseDto[];
  onPosted: () => Promise<void>;
}) {
  const unresolvedCount = comments.filter((c) => !c.resolved).length;
  const totalCount = comments.length;
  const countLabel = `${unresolvedCount} open of ${totalCount} comment${totalCount === 1 ? "" : "s"}`;

  return (
    <div
      className="mt-space-4 rounded-lg border border-status-warning/30 bg-status-warning/10 p-space-4"
      data-testid="return-comments-banner"
    >
      <div className="flex flex-col gap-space-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="m-0 text-small font-semibold text-onyx">
            This deal was returned for changes — {countLabel} from your liaison.
          </p>
          <p className="m-0 mt-space-1 text-caption text-gray-mid">
            Review the requested changes in the affected wizard sections before resubmitting.
          </p>
        </div>
        <Button size="sm" onClick={onViewComments} data-testid="view-liaison-comments">
          View liaison comments
        </Button>
      </div>
      {overallComments.length > 0 && (
        <div className="mt-space-3">
          <ReturnCommentsPanel
            comments={overallComments}
            onResolve={onResolve}
            title="Overall liaison comments"
            submissionId={submissionId}
            responses={responses}
            onPosted={onPosted}
          />
        </div>
      )}
    </div>
  );
}

function ReturnCommentsPanel({
  comments,
  onResolve,
  title = "Liaison comments",
  submissionId,
  responses,
  onPosted,
}: {
  comments: PortalDealSubmissionReturnCommentDto[];
  onResolve: (commentId: string) => Promise<void>;
  title?: string;
  submissionId: string;
  responses: DealSubmissionResponseDto[];
  onPosted: () => Promise<void>;
}) {
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  async function resolve(commentId: string) {
    setResolvingId(commentId);
    try {
      await onResolve(commentId);
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <section
      className="rounded-md border border-gold/30 bg-gold/10 p-space-3"
      data-testid="liaison-comments-panel"
    >
      <h3 className="m-0 text-small font-semibold text-onyx">{title}</h3>
      <ul className="m-0 mt-space-2 flex list-none flex-col gap-space-2 p-0">
        {comments.map((comment) => (
          <li
            key={comment.id}
            className="rounded-md border border-border-light bg-white-pure p-space-3"
            data-testid={`liaison-comment-${comment.id}`}
          >
            <p className="m-0 flex flex-wrap items-center gap-space-2 text-caption text-gray-mid">
              <span className="font-semibold text-onyx">{comment.authorDisplayName ?? "Liaison"}</span>
              <span aria-hidden>·</span>
              {/* INSTANT (DateTimeOffset stamped from DateTimeOffset.UtcNow in
                  DealSubmissionStagingService), so it renders in a zone — and it
                  MUST be the same zone the broker's other screen uses. The
                  sibling render of this very field is BrokerDealStatusPage's
                  `CommentMeta`, which puts `comment.createdAt` through the shared
                  <Timestamp> → formatDateTimeInTz. A bare toLocaleString() here
                  took the browser's zone instead, so one comment read as two
                  different days on the two screens a broker moves between.
                  The zone is an EXPLICIT null: the portal has no viewer zone to
                  resolve (PortalUser carries no timezone column and PortalMeDto
                  carries no TimeZone field), so FALLBACK_TIMEZONE is the honest
                  answer and the `null` says so out loud where an omission would
                  say nothing. */}
              <span>{formatDateTimeInTz(comment.createdAt, null)}</span>
              {comment.resolved && (
                <>
                  <span aria-hidden>·</span>
                  <span className="font-semibold text-status-success">Resolved</span>
                </>
              )}
            </p>
            <p className="m-0 mt-space-1 whitespace-pre-wrap text-small text-onyx">{comment.body}</p>
            <CommentResponder
              submissionId={submissionId}
              comment={comment}
              responses={responses.filter((r) => r.returnCommentId === comment.id)}
              onPosted={onPosted}
            />
            {!comment.resolved && (
              <Button
                className="mt-space-2"
                size="sm"
                variant="secondary"
                onClick={() => void resolve(comment.id)}
                disabled={resolvingId === comment.id}
                data-testid={`mark-comment-resolved-${comment.id}`}
              >
                {resolvingId === comment.id ? "Marking…" : "Mark resolved"}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// Append-only broker reply, threaded under one liaison comment. Shows the
// broker's prior replies and a "Respond" form (text + optional PDF/DOCX).
// Posting NEVER reopens wizard sections — it only appends an artifact the
// liaison views and files in Deal Staging.
function CommentResponder({
  submissionId,
  comment,
  responses,
  onPosted,
}: {
  submissionId: string;
  comment: PortalDealSubmissionReturnCommentDto;
  responses: DealSubmissionResponseDto[];
  onPosted: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const postResponse = useSubmittingMutation(
    async () => {
      await dealSubmissionApi.postCommentResponse(submissionId, comment.id, body.trim(), file);
      setBody("");
      setFile(null);
      setOpen(false);
    },
    {
      refresh: onPosted,
      onError: (err) =>
        setError(
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Couldn't post your response."),
    },
  );
  const submitting = postResponse.pending;

  function submit() {
    if (!body.trim() && !file) {
      setError("Add a comment or attach a document.");
      return;
    }
    setError(null);
    void postResponse.submit();
  }

  return (
    <div className="mt-space-2" data-testid={`comment-responder-${comment.id}`}>
      {responses.length > 0 && (
        <ul className="m-0 mb-space-2 flex list-none flex-col gap-space-1 p-0">
          {responses.map((r) => (
            <li
              key={r.id}
              className="rounded-md border border-border-light bg-white-warm p-space-2"
              data-testid={`broker-response-${r.id}`}
            >
              <p className="m-0 text-caption text-gray-mid">
                <span className="font-semibold text-onyx">Your response</span>
                <span aria-hidden> · </span>
                {/* Same INSTANT treatment, same explicit-null zone
                    (DealSubmissionResponseService stamps CreatedAt from
                    DateTimeOffset.UtcNow). This row is nested INSIDE the
                    liaison-comment <li> above, so its nearest neighbour is that
                    comment's own timestamp — a browser-zone reply printed under
                    a Phoenix-zone comment would disagree with its own parent.
                    Staff render this same DealSubmissionResponseDto.CreatedAt on
                    DealStagingReviewPage's ResponseCard through the staff tz hook
                    (useDateFormatter), which resolves the signed-in user's zone;
                    the portal has no equivalent because it has no viewer zone to
                    resolve, which is exactly why this passes null. */}
                <span>{formatDateTimeInTz(r.createdAt, null)}</span>
              </p>
              {r.body && (
                <p className="m-0 mt-space-1 whitespace-pre-wrap text-small text-onyx">{r.body}</p>
              )}
              {r.hasDocument && (
                <p className="m-0 mt-space-1 text-caption text-gray-mid">
                  📎 {r.originalFileName}
                  <span aria-hidden> · </span>
                  {r.scanStatus === "Clean" ? "Uploaded" : r.scanStatus === "Infected"
                    ? "Rejected (infected)" : r.scanStatus === "Error" ? "Scan error" : "Scanning…"}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {open ? (
        <div className="rounded-md border border-border-light bg-white-pure p-space-3">
          <label className="block text-caption font-semibold text-onyx" htmlFor={`resp-body-${comment.id}`}>
            Your response
          </label>
          <textarea
            id={`resp-body-${comment.id}`}
            className="mt-space-1 w-full rounded-md border border-border-light p-space-2 text-small text-onyx"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid={`response-body-${comment.id}`}
          />
          <label className="mt-space-2 block text-caption font-semibold text-onyx" htmlFor={`resp-file-${comment.id}`}>
            Attach a document (optional, PDF or DOCX)
          </label>
          <input
            id={`resp-file-${comment.id}`}
            type="file"
            accept=".pdf,.docx"
            className="mt-space-1 block text-caption text-gray-mid"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            data-testid={`response-file-${comment.id}`}
          />
          {error && <p className="m-0 mt-space-2 text-caption text-status-error" role="alert">{error}</p>}
          <div className="mt-space-2 flex gap-space-2">
            <SubmitButton
              size="sm"
              onClick={submit}
              pending={submitting}
              pendingLabel="Sending…"
              data-testid={`response-submit-${comment.id}`}
            >
              Send response
            </SubmitButton>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setOpen(false); setError(null); }}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
          <SubmittingHint pending={submitting} className="mt-space-2" />
        </div>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setOpen(true)}
          data-testid={`respond-to-comment-${comment.id}`}
        >
          Respond
        </Button>
      )}
    </div>
  );
}

function groupReturnCommentsBySection(data: DealSubmissionRichDto) {
  const groups: Record<string, PortalDealSubmissionReturnCommentDto[]> = {};
  for (const comment of data.returnComments) {
    const sectionId = commentTargetSectionId(comment, data);
    if (!sectionId) continue;
    groups[sectionId] = [...(groups[sectionId] ?? []), comment];
  }
  return groups;
}

function commentTargetSectionId(
  comment: PortalDealSubmissionReturnCommentDto,
  data: DealSubmissionRichDto,
): string | null {
  if (comment.sectionId) return comment.sectionId;
  if (!comment.documentRequestId) return null;
  return data.documentRequests.find((r) => r.id === comment.documentRequestId)?.sectionId ?? null;
}

// RECON→FIX (UAT 2026-05-30) — render the submission status as a chip
// (Draft / Submitted / …) rather than the plain "Status: Draft" text.
// Inline rather than the shared Badge primitive, which currently renders
// its variant class as text instead of applying it.
function StatusChip({ status }: { status: string }) {
  const tone =
    status === "Submitted" || status === "InStaging"
      ? "bg-status-success/10 text-status-success border-status-success/30"
      : status === "ReturnedForChanges"
        ? "bg-gold/10 text-gold-shadow border-gold/30"
        : "bg-white-warm text-gray-mid border-border-light";
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-space-2 py-[2px] text-caption font-medium ${tone}`}
      data-testid="wizard-status-chip"
    >
      {humanizeStatus(status)}
    </span>
  );
}

function humanizeStatus(status: string): string {
  // Space out PascalCase enum values (ReturnedForChanges → Returned for changes).
  const spaced = status.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function sectionKey(s: DealSubmissionSectionDto): string {
  return String(s.order);
}

function sectionKeyForOrder(sections: DealSubmissionSectionDto[], order: number): string {
  if (sections.length === 0) return REVIEW_KEY;
  const exact = sections.find((s) => s.order === order);
  if (exact) return sectionKey(exact);
  return sectionKey(sections[0]);
}

export const REVIEW_SECTION_KEY = REVIEW_KEY;
